import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { useSearchParams } from "react-router-dom";
import {
  BookmarkCheck,
  BookmarkPlus,
  AtSign,
  Boxes,
  ChevronDown,
  FileText,
  Film,
  History,
  Library,
  MessageSquare,
  Plus,
  Repeat2,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import { ModeTabs, type Modality } from "@/components/ModeTabs";
import { galleryCursor, galleryPageKey, matchesGalleryMedia } from "@/lib/galleryQuery";
import { ModelPicker } from "@/components/ModelPicker";
import { ProviderPicker } from "@/components/ProviderPicker";
import { CapabilityForm } from "@/components/CapabilityForm";
import { MentionPicker } from "@/components/MentionPicker";
import {
  PromptEnhancerResult,
  PromptEnhancerToggle,
  usePromptEnhancer,
} from "@/components/PromptEnhancer";
import { GenerateButton } from "@/components/GenerateButton";
import { CostConfirmationDialog } from "@/components/CostConfirmationDialog";
import { TrashSelectionDialog } from "@/components/TrashSelectionDialog";
import { ResultGrid } from "@/components/ResultGrid";
import { LoadMoreSentinel } from "@/components/LoadMoreSentinel";
import { Dialog } from "@/components/ui/dialog";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useJobEvents } from "@/hooks/useJobEvents";
import { useProjectStore } from "@/store/project";
import { useAuthStore } from "@/store/auth";
import { useRecreateStore } from "@/store/recreate";
import { usePreferences } from "@/hooks/usePreferences";
import { StudioQuickControls } from "@/components/studio/StudioQuickControls";
import { StudioDirectionPicker } from "@/components/studio/StudioDirectionPicker";
import {
  EMPTY_DIRECTIONS,
  directionCameraLabel,
  directionLookLabel,
  directionsFromParams,
  directionsForModality,
  quickParamNames,
  resetVisualDirections,
  withCreativeDirection,
  type DirectionSelections,
} from "@/components/studio/studioConfig";
import { StudioDirectorPanel } from "@/components/studio/StudioDirectorPanel";
import { ProjectBriefPanel } from "@/components/studio/ProjectBriefPanel";
import { StudioAssetPicker, type AssetPickerTab } from "@/components/studio/StudioAssetPicker";
import { PromptEditor, type PromptEditorHandle } from "@/components/studio/PromptEditor";
import { ComposerChips } from "@/components/studio/ComposerChips";
import { SelectionToolbar } from "@/components/SelectionToolbar";
import { MoveToProjectDialog } from "@/components/MoveToProjectDialog";
import { MoveResultBar } from "@/components/MoveResultBar";
import { useProjectMove } from "@/hooks/useProjectMove";
import { sharedProjectId } from "@/lib/projectMove";
import { useBulkDownload } from "@/hooks/useBulkDownload";
import { useJobSelection } from "@/hooks/useJobSelection";
import { downloadPlan } from "@/lib/selection";
import {
  swrFetcher,
  apiFetch,
  createGenerationQuote,
  submitQuotedJob,
  trashJobs,
  type CostQuoteInfo,
  type ModelInfo,
  type CapabilityInfo,
  type JobInfo,
  type LibraryStudioPayload,
  ApiError,
  errorMessage,
} from "@/lib/api";
import { moveJobs } from "@/lib/api";
import { cn } from "@/lib/utils";
import { isCreativeParameter } from "@/lib/capabilitySections";
import { recreateDraft } from "@/lib/recreate";
import { validatedReferenceMessage, type ReferenceEstimate } from "@/lib/referenceRequirements";
import { bindReference, listMentionSlugs, referenceBindings, updateDraftParam } from "@/lib/mentions";
import { GenerationCount } from "@/components/studio/GenerationCount";
import { generationCount, NATIVE_COUNT_PARAMS } from "@/lib/batches";

const ZONE_LABEL = "select-none text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted";
/** First page polls every 3s; older pages load on demand as the canvas scrolls. */
const STUDIO_PAGE_SIZE = 24;
const ZONE_DIVIDER = "h-6 w-px shrink-0 bg-studio-border";

function defaultsFor(capability: CapabilityInfo | null | undefined): Record<string, unknown> {
  if (!capability) return {};
  return Object.fromEntries(
    capability.ui_schema.params
      .filter((param) => param.default !== undefined)
      .map((param) => [param.name, param.default])
  );
}

function toStudioModality(value: string | undefined): Modality {
  return value === "video" || value === "audio" ? value : "image";
}

function modalityForCapability(capabilityKey: string, modalities: string[]): Modality {
  const key = capabilityKey.toLowerCase();
  const derived: Modality = key.includes("video")
    ? "video"
    : key.includes("audio") || key.includes("speech") || key.includes("tts")
      ? "audio"
      : "image";
  if (modalities.includes(derived)) return derived;
  return toStudioModality(modalities.find((value) => ["image", "video", "audio"].includes(value)));
}

type StudioPanel = "brief" | "director" | "recent" | null;
type WorkspaceMode = "create" | "chat";
type RecreateNotice = { tone: "info" | "error"; message: string };

export function StudioPage() {
  const ownerContext = useAuthStore((state) => `${state.user?.id}:${state.activeTeam?.id}:${state.activeTeam?.role}`);
  const [searchParams, setSearchParams] = useSearchParams();
  const libraryItemId = searchParams.get("library_item");
  const libraryTarget = searchParams.get("library_target");
  const librarySelectionId = libraryItemId ? `${libraryItemId}:${libraryTarget ?? "recipe"}` : null;
  const [modality, setModality] = useState<Modality>("image");
  const galleryMedia = modality;
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>("create");
  const [composerCollapsed, setComposerCollapsed] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedCapability, setSelectedCapability] = useState<string | null>(null);
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [countInput, setCountInput] = useState("1");
  const outputCount = generationCount(countInput);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [walletAvailable, setWalletAvailable] = useState<number | null>(null);
  const [estimatedCost, setEstimatedCost] = useState<number | undefined>();
  const [referenceEstimate, setReferenceEstimate] = useState<ReferenceEstimate | null>(null);
  const [costQuote, setCostQuote] = useState<CostQuoteInfo | null>(null);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [enhancerSaving, setEnhancerSaving] = useState(false);
  const [threadInteractionId, setThreadInteractionId] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [outputOpen, setOutputOpen] = useState(false);
  const [panel, setPanel] = useState<StudioPanel>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [assetPickerTab, setAssetPickerTab] = useState<AssetPickerTab>("elements");
  const [directionOpen, setDirectionOpen] = useState(false);
  const [directions, setDirections] = useState<DirectionSelections>(EMPTY_DIRECTIONS);
  const [modalityInitialized, setModalityInitialized] = useState(false);
  const [recreateNotice, setRecreateNotice] = useState<RecreateNotice | null>(null);
  const pendingInitialModality = useRef<Modality | null>(null);
  const appliedLibraryItem = useRef<string | null>(null);
  const mergedLibraryItem = useRef<string | null>(null);
  const promptRef = useRef<PromptEditorHandle>(null);

  const pendingRecreateJob = useRecreateStore((state) => state.pendingJob);
  const pendingReference = useRecreateStore((state) => state.pendingReference);
  const clearReference = useRecreateStore((state) => state.clearReference);
  const clearRecreate = useRecreateStore((state) => state.clearRecreate);
  const selectedProjectId = useProjectStore((state) => state.selectedProjectId);
  const { preferences, update: updatePreferences } = usePreferences();
  const { data: libraryPayload, error: libraryError } = useSWR<LibraryStudioPayload>(
    libraryItemId ? `/api/library/items/${libraryItemId}/studio-payload${libraryTarget ? `?target=${encodeURIComponent(libraryTarget)}` : ""}` : null,
    swrFetcher
  );
  const { data: allModels } = useSWR<ModelInfo[]>("/api/capabilities", swrFetcher);
  const referenceModels = (allModels ?? []).filter((model) => model.modalities.includes("video") && model.capability_keys?.some((key) => key === "reference_to_video" || key === "image_to_video"));
  const referenceModel = referenceModels.find((model) => model.key === selectedModel) ?? referenceModels[0];
  const { data: referenceCapabilities, error: referenceCapabilitiesError } = useSWR<CapabilityInfo[]>(
    pendingReference && referenceModel ? `/api/capabilities/${encodeURIComponent(referenceModel.key)}/capabilities` : null, swrFetcher);
  const { data: recentPages, mutate: mutateRecent, size: recentSize, setSize: setRecentSize } = useSWRInfinite<JobInfo[]>(
    (index, previous) =>
      previous && previous.length < STUDIO_PAGE_SIZE
        ? null
        : galleryPageKey({ filter: "all", media: galleryMedia, projectId: selectedProjectId, page: index, pageSize: STUDIO_PAGE_SIZE, cursor: galleryCursor(previous) }),
    swrFetcher,
    { refreshInterval: 3000, revalidateFirstPage: true }
  );
  // Pages chain by cursor; dedupe still guards the moment a refetched page lands.
  const recentJobs = useMemo(() => {
    const seen = new Set<string>();
    return (recentPages ?? []).flat().filter((job) => (seen.has(job.id) ? false : (seen.add(job.id), true)));
  }, [recentPages]);
  const recentLastPage = recentPages?.[recentPages.length - 1];
  const hasMoreRecent = !!recentLastPage && recentLastPage.length === STUDIO_PAGE_SIZE;
  const loadingMoreRecent = recentSize > 0 && !!recentPages && recentPages[recentSize - 1] === undefined;
  const loadMoreRecent = useCallback(() => {
    void setRecentSize((current) => current + 1);
  }, [setRecentSize]);
  const loadMoreRecentForViewer = useCallback(() => setRecentSize((current) => current + 1), [setRecentSize]);
  const { data: models } = useSWR<ModelInfo[]>(`/api/capabilities?modality=${modality}`, swrFetcher);
  const { data: capabilities } = useSWR<CapabilityInfo[]>(
    selectedModel ? `/api/capabilities/${encodeURIComponent(selectedModel)}/capabilities` : null,
    swrFetcher
  );
  const { data: wallet } = useSWR<{ available_usd: string }>("/api/wallet/balance", swrFetcher, { refreshInterval: 5000 });
  const { data: recreateCapabilities, error: recreateCapabilitiesError } = useSWR<CapabilityInfo[]>(
    pendingRecreateJob
      ? `/api/capabilities/${encodeURIComponent(pendingRecreateJob.model_key)}/capabilities`
      : null,
    swrFetcher
  );

  useEffect(() => {
    if (wallet?.available_usd != null) setWalletAvailable(Number(wallet.available_usd));
  }, [wallet]);

  useEffect(() => {
    if (!pendingReference || !allModels) return;
    if (!referenceModel || referenceCapabilitiesError) {
      setRecreateNotice({ tone: "error", message: "No accessible video workflow can load this image reference." });
      clearReference();
      return;
    }
    if (!referenceCapabilities) return;
    const capability = referenceCapabilities.find((item) => item.key === "reference_to_video")
      ?? referenceCapabilities.find((item) => item.key === "image_to_video");
    const field = capability?.ui_schema.params.find((item) => item.name === "reference_images")
      ?? capability?.ui_schema.params.find((item) => item.name === "image");
    if (!capability || !field) {
      setRecreateNotice({ tone: "error", message: "This video workflow has no image input. Select a reference-capable workflow." });
      clearReference(); return;
    }
    const current: Record<string, unknown> = referenceModel.key === selectedModel && modality === "video" ? params : { prompt: typeof params.prompt === "string" ? params.prompt : "" };
    const binding = bindReference(current, { token: `@image-${pendingReference.asset_id.slice(0,8)}`, kind: "asset", id: pendingReference.asset_id });
    const prompt = typeof current.prompt === "string" ? current.prompt : "";
    const existing = Array.isArray(current[field.name]) ? current[field.name] as Record<string, unknown>[] : [];
    const nextParams = { ...defaultsFor(capability), ...current,
      [field.name]: field.widget === "multi_upload" ? [...existing.filter((item) => item.asset_id !== pendingReference.asset_id), pendingReference] : pendingReference,
      reference_bindings: [...referenceBindings(current).filter((item) => item.token !== binding.token), binding],
      prompt: listMentionSlugs(prompt).includes(binding.token.slice(1)) ? prompt : `${prompt}${prompt && !/\s$/.test(prompt) ? " " : ""}${binding.token} ` };
    setModalityInitialized(true); pendingInitialModality.current = null;
    setWorkspaceMode("create"); setModality("video"); setSelectedProvider(referenceModel.provider);
    setSelectedModel(referenceModel.key); setSelectedCapability(capability.key); setParams(nextParams);
    setDirections(directionsFromParams(nextParams)); setThreadInteractionId(null);
    setCostDialogOpen(false); setCostQuote(null); setSubmitError(null); setAssetPickerOpen(false);
    if (libraryItemId) { const next = new URLSearchParams(searchParams); next.delete("library_item"); next.delete("library_target"); setSearchParams(next, { replace: true }); }
    clearReference(); setRecreateNotice({ tone: "info", message: "Image added to your video draft as a reference" });
  }, [pendingReference, allModels, referenceModel, referenceCapabilities, referenceCapabilitiesError, selectedModel, modality, params, clearReference, libraryItemId, librarySelectionId, searchParams, setSearchParams]);

  useEffect(() => {
    if (pendingReference || libraryItemId || modalityInitialized || !preferences || !allModels) return;
    const pinnedFirst = preferences.model_memory === "pinned";
    const workflowModality = preferences.default_workflow?.modality
      ? toStudioModality(preferences.default_workflow.modality)
      : undefined;
    const lastModality = (["image", "video", "audio"] as Modality[])
      .find((value) => Boolean(preferences.last_workflows?.[value]));
    const favorite = allModels.find((model) => model.key === preferences.favorite_model_key);
    const favoriteModality = favorite?.modalities.find((value) => ["image", "video", "audio"].includes(value)) as Modality | undefined;
    const modalityOrder = pinnedFirst
      ? [workflowModality, lastModality, favoriteModality]
      : [lastModality, workflowModality, favoriteModality];
    const directionModality = searchParams.get("direction_modality");
    const initialModality = directionModality === "video" || directionModality === "image" ? directionModality : modalityOrder.find(Boolean) ?? "image";
    pendingInitialModality.current = initialModality;
    setModality(initialModality);
    setModalityInitialized(true);
  }, [preferences, allModels, libraryItemId, librarySelectionId, modalityInitialized, pendingReference, searchParams]);

  useEffect(() => {
    if (!libraryItemId || !libraryPayload || !allModels || appliedLibraryItem.current === librarySelectionId) return;
    const libraryModel = allModels.find((model) => model.key === libraryPayload.model_key);
    if (!libraryModel) return;
    setModalityInitialized(true);
    pendingInitialModality.current = null;
    appliedLibraryItem.current = librarySelectionId;
    mergedLibraryItem.current = null;
    setModality(toStudioModality(libraryPayload.modality));
    setSelectedProvider(libraryModel.provider);
    setSelectedModel(libraryPayload.model_key);
    setSelectedCapability(libraryPayload.capability_key);
    setParams(libraryPayload.params);
    setComposerCollapsed(false);
    setDirections(directionsFromParams(libraryPayload.params));
    setThreadInteractionId(null);
    setSubmitError(null);
  }, [allModels, libraryItemId, librarySelectionId, libraryPayload]);

  useEffect(() => {
    if (!pendingRecreateJob || !allModels) return;
    const job = pendingRecreateJob;
    const model = allModels.find((entry) => entry.key === job.model_key);
    if (!model) {
      clearRecreate();
      setRecreateNotice({
        tone: "error",
        message: `${job.model_key} is no longer available to this team, so nothing was restored.`,
      });
      return;
    }
    if (recreateCapabilitiesError) {
      clearRecreate();
      setRecreateNotice({
        tone: "error",
        message: `The workflow behind this generation could not be loaded, so nothing was restored.`,
      });
      return;
    }
    if (!recreateCapabilities) return;
    const capability = recreateCapabilities.find((entry) => entry.key === job.capability_key);
    if (!capability) {
      clearRecreate();
      setRecreateNotice({
        tone: "error",
        message: `${model.display_name} no longer offers "${job.capability_key}", so nothing was restored.`,
      });
      return;
    }

    const nextModality = modalityForCapability(job.capability_key, model.modalities);
    const draft = recreateDraft(job.params, nextModality);
    setComposerCollapsed(false);
    const restored = directionsFromParams(job.params);

    setModalityInitialized(true);
    pendingInitialModality.current = null;
    setModality(nextModality);
    setSelectedProvider(model.provider);
    setSelectedModel(model.key);
    setSelectedCapability(capability.key);
    setParams({ ...defaultsFor(capability), ...draft.params });
    setDirections(draft.genre ? { ...restored, genre: draft.genre } : restored);
    setThreadInteractionId(null);
    setSubmitError(null);
    setCostDialogOpen(false);
    setCostQuote(null);
    setPanel(null);
    setAssetPickerOpen(false);
    setAdvancedOpen(false);
    if (libraryItemId) {
      const next = new URLSearchParams(searchParams);
      next.delete("library_item"); next.delete("library_target");
      setSearchParams(next, { replace: true });
    }
    clearRecreate();
    setRecreateNotice({ tone: "info", message: `${model.display_name} · ${capability.label}` });
  }, [
    allModels,
    clearRecreate,
    libraryItemId,
    pendingRecreateJob,
    recreateCapabilities,
    recreateCapabilitiesError,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (libraryItemId && appliedLibraryItem.current !== librarySelectionId) return;
    if (pendingReference || !preferences || !models?.length || selectedModel || selectedProvider || !modalityInitialized) return;
    if (pendingInitialModality.current && pendingInitialModality.current !== modality) return;
    pendingInitialModality.current = null;
    const defaultModel = preferences.default_workflow?.modality === modality
      ? preferences.default_workflow.model_key
      : null;
    const last = preferences.last_workflows?.[modality];
    const candidates = preferences.model_memory === "pinned"
      ? [defaultModel, last?.model_key, preferences.favorite_model_key]
      : [last?.model_key, defaultModel, preferences.favorite_model_key];
    const candidate = candidates
      .map((key) => models.find((model) => model.key === key))
      .find(Boolean);
    if (candidate) {
      setSelectedProvider(candidate.provider);
      setSelectedModel(candidate.key);
    }
  }, [models, modality, preferences, selectedModel, selectedProvider, libraryItemId, librarySelectionId, modalityInitialized, pendingReference]);

  useEffect(() => {
    if (libraryItemId && appliedLibraryItem.current !== librarySelectionId) return;
    if (pendingReference || !preferences || !capabilities?.length || selectedCapability) return;
    const last = preferences.last_workflows?.[modality];
    const pinnedCapability = preferences.default_workflow?.model_key === selectedModel
      ? preferences.default_workflow.capability_key
      : null;
    const lastCapability = last?.model_key === selectedModel ? last.capability_key : null;
    const preferred = preferences.model_memory === "pinned"
      ? pinnedCapability ?? lastCapability
      : lastCapability ?? pinnedCapability;
    const capability = capabilities.find((item) => item.key === preferred) ?? capabilities[0];
    setSelectedCapability(capability.key);
    setParams(defaultsFor(capability));
  }, [capabilities, modality, preferences, selectedCapability, selectedModel, libraryItemId, librarySelectionId, pendingReference]);

  useEffect(() => {
    if (!preferences || !selectedProvider || !selectedModel || !selectedCapability || libraryItemId) return;
    const current = preferences.last_workflows?.[modality];
    if (
      current?.provider_key === selectedProvider
      && current.model_key === selectedModel
      && current.capability_key === selectedCapability
    ) return;
    void updatePreferences({
      last_workflows: {
        [modality]: {
          provider_key: selectedProvider,
          model_key: selectedModel,
          capability_key: selectedCapability,
        },
      },
    }).catch(() => {
      // Non-fatal, but no longer silent: the workflow simply is not remembered.
      setSubmitError(null);
    });
  }, [
    libraryItemId,
    modality,
    preferences,
    selectedCapability,
    selectedModel,
    selectedProvider,
    updatePreferences,
  ]);

  useEffect(() => {
    if (searchParams.get("creative_direction") !== "1") return;
    if (modality === "audio") {
      setModality("image");
      setSelectedProvider(null);
      setSelectedModel(null);
      setSelectedCapability(null);
      setParams({});
      setDirections((current) => directionsForModality(current, "image"));
      return;
    }
    if (!modalityInitialized) return;
    setDirectionOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("creative_direction");
    setSearchParams(next, { replace: true });
  }, [modality, modalityInitialized, searchParams, selectedCapability, selectedModel, setSearchParams]);

  useEffect(() => {
    if (!libraryItemId || !libraryPayload || !capabilities || mergedLibraryItem.current === librarySelectionId) return;
    const capability = capabilities.find((item) => item.key === libraryPayload.capability_key);
    if (!capability) return;
    mergedLibraryItem.current = librarySelectionId;
    setParams({ ...defaultsFor(capability), ...libraryPayload.params });
  }, [capabilities, libraryItemId, librarySelectionId, libraryPayload]);

  const handleModalityChange = (next: Modality) => {
    setModality(next);
    setDirections((current) => directionsForModality(current, next));
    setSelectedProvider(null);
    setSelectedModel(null);
    setSelectedCapability(null);
    setParams({});
    setThreadInteractionId(null);
    setAdvancedOpen(false);
    setCostDialogOpen(false);
    setCostQuote(null);
  };

  const handleProviderChange = (providerKey: string) => {
    if (providerKey === selectedProvider) return;
    setSelectedProvider(providerKey);
    setSelectedModel(null);
    setSelectedCapability(null);
    setParams({});
    setDirections((current) => resetVisualDirections(current));
    setThreadInteractionId(null);
    setAdvancedOpen(false);
    setOutputOpen(false);
    setCostDialogOpen(false);
    setCostQuote(null);
    setSubmitError(null);
  };

  const handleModelChange = (modelKey: string) => {
    setSelectedModel(modelKey);
    setSelectedCapability(null);
    setParams({});
    setDirections((current) => resetVisualDirections(current));
    setThreadInteractionId(null);
    setCostDialogOpen(false);
    setCostQuote(null);
    setSubmitError(null);
  };

  const handleWorkspaceModeChange = (next: WorkspaceMode) => {
    setWorkspaceMode(next);
    if (next === "create") setAdvancedOpen(false);
  };

  const handleParamChange = (name: string, value: unknown) => {
    setParams((current) => updateDraftParam(current, name, value));
  };

  const currentCapability = capabilities?.find((capability) => capability.key === selectedCapability) ?? null;
  const currentModel = models?.find((model) => model.key === selectedModel) ?? allModels?.find((model) => model.key === selectedModel);
  const providerModels = (models ?? []).filter((model) => model.provider === selectedProvider);
  const promptParam = currentCapability?.ui_schema.params.find((param) => param.name === "prompt");
  const hasPrompt = Boolean(promptParam);
  const currentPrompt = typeof params.prompt === "string" ? params.prompt : "";
  const promptMissing = Boolean(promptParam?.required && !currentPrompt.trim());
  const quickNames = quickParamNames(currentCapability);
  const singleOutputCapability = currentCapability ? { ...currentCapability, ui_schema: { ...currentCapability.ui_schema,
    params: currentCapability.ui_schema.params.filter((param) => !NATIVE_COUNT_PARAMS.includes(param.name)) } } : null;
  const framingCapability = currentCapability ? { ...currentCapability, ui_schema: { ...currentCapability.ui_schema,
    params: currentCapability.ui_schema.params.filter((param) => ["ratio", "aspect_ratio", "image_size"].includes(param.name) && (param.options?.length ?? 0) > 0) } } : null;
  const framingNames = framingCapability?.ui_schema.params.map((param) => param.name) ?? [];
  const uploadNames = currentCapability?.ui_schema.params
    .filter((param) => param.widget === "upload" || param.widget === "multi_upload")
    .map((param) => param.name) ?? [];
  const advancedOmissions = ["prompt", "camera", "camera_manual", "camera_presets", ...quickNames, ...uploadNames, ...NATIVE_COUNT_PARAMS];
  const advancedCount = currentCapability?.ui_schema.params.filter((param) => !advancedOmissions.includes(param.name) && param.widget !== "hidden" && param.widget !== "camera_presets" && (param.name !== "negative_prompt" || param.native_support !== false)).length ?? 0;
  const technicalParamCount = singleOutputCapability?.ui_schema.params.filter((param) => !isCreativeParameter(param) && !framingNames.includes(param.name) && param.widget !== "hidden" && param.widget !== "camera_presets" && (param.name !== "negative_prompt" || param.native_support !== false)).length ?? 0;
  const lookDirectionLabel = directionLookLabel(directions);
  const cameraDirectionLabel = directionCameraLabel(directions, modality);
  const effectiveParams = useMemo(
    () => withCreativeDirection({ ...params, ...Object.fromEntries((currentCapability?.ui_schema.params ?? [])
      .filter((param) => NATIVE_COUNT_PARAMS.includes(param.name))
      .map((param) => [param.name, param.name === "sequential_image_generation" ? "disabled" : 1])) }, directions, modality),
    [params, directions, modality, currentCapability]
  );
  const enhancerSupported = Boolean(
    hasPrompt
    && selectedModel
    && selectedCapability
    && (
      (currentModel?.provider === "byteplus_ark"
        && (selectedModel.startsWith("byteplus-seedance-") || selectedModel.startsWith("byteplus-seedream-")))
      || (currentModel?.provider === "openai" && selectedModel.startsWith("gpt-image-2.5-"))
    )
  );
  const enhancerEnabled = enhancerSupported && (preferences?.enhance_before_generation ?? false);
  const enhancerChangeKey = useMemo(
    () => JSON.stringify([
      Object.fromEntries(Object.entries(params).filter(([name]) => name !== "prompt")),
      directions,
    ]),
    [directions, params]
  );
  const enhancer = usePromptEnhancer({
    modelKey: selectedModel ?? "",
    capabilityKey: selectedCapability ?? "",
    prompt: currentPrompt,
    params: effectiveParams,
    changeKey: enhancerChangeKey,
    projectId: selectedProjectId,
    onApply: (value) => handleParamChange("prompt", value),
  });

  const handleEnhancerToggle = async (checked: boolean) => {
    setEnhancerSaving(true);
    setSubmitError(null);
    try {
      await updatePreferences({ enhance_before_generation: checked });
      if (!checked) enhancer.discard();
    } catch (cause) {
      setSubmitError(cause instanceof ApiError ? cause.message : "Enhancement preference could not be saved");
    } finally {
      setEnhancerSaving(false);
    }
  };

  useEffect(() => {
    if (!libraryItemId || !libraryPayload || !currentCapability || mergedLibraryItem.current !== librarySelectionId) return;
    const timer = window.setTimeout(() => {
      const name = libraryPayload.focus_param.replace(/[^a-zA-Z0-9_-]/g, "");
      const element = document.querySelector<HTMLElement>(`[data-param-name="${name}-trigger"]`)
        ?? document.querySelector<HTMLElement>(`[data-param-name="${name}"]`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      element?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [currentCapability, libraryItemId, librarySelectionId, libraryPayload]);

  const isDefaultWorkflow = Boolean(
    preferences?.default_workflow
    && preferences.default_workflow.modality === modality
    && preferences.default_workflow.model_key === selectedModel
    && preferences.default_workflow.capability_key === selectedCapability
  );

  const memoryPinned = preferences?.model_memory === "pinned";

  const setCurrentAsDefault = async () => {
    if (!selectedModel || !selectedCapability) return;
    if (isDefaultWorkflow && memoryPinned) {
      await updatePreferences({ model_memory: "last_used" });
      return;
    }
    await updatePreferences({
      model_memory: "pinned",
      default_workflow: { modality, model_key: selectedModel, capability_key: selectedCapability },
    });
  };

  const memoryLabel = memoryPinned && isDefaultWorkflow ? "Pinned" : "Last used";
  const estimateKey = JSON.stringify([ownerContext, selectedModel, selectedCapability, effectiveParams]);

  useEffect(() => {
    if (!selectedModel || !selectedCapability || promptMissing) {
      setEstimatedCost(undefined);
      setReferenceEstimate(null);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void apiFetch<{ estimated_usd: string; reference_images: unknown[] }>(
        `/api/capabilities/${encodeURIComponent(selectedModel)}/capabilities/${encodeURIComponent(selectedCapability)}/estimate`,
        { method: "POST", body: JSON.stringify({ params: effectiveParams }) }
      ).then((estimate) => {
        if (active) {
          setEstimatedCost(Number(estimate.estimated_usd));
          setReferenceEstimate({ key: estimateKey, images: estimate.reference_images ?? [] });
        }
      }).catch((cause) => {
        if (active) {
          setEstimatedCost(undefined);
          setReferenceEstimate(cause instanceof ApiError && cause.status === 422
            ? { key: estimateKey, images: [], error: errorMessage(cause, "Could not validate references.") }
            : null);
        }
      });
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [selectedModel, selectedCapability, effectiveParams, estimateKey, promptMissing]);

  const totalEstimatedCost = estimatedCost == null || outputCount == null ? undefined : estimatedCost * outputCount;
  const insufficientBalance = totalEstimatedCost != null && walletAvailable != null && walletAvailable < totalEstimatedCost;
  const { job: activeJob, pct, message, partials, streaming } = useJobEvents(activeJobId);

  useEffect(() => {
    if (!activeJob || !["completed", "failed", "cancelled"].includes(activeJob.state)) return;
    void mutateRecent();
    setActiveJobId(null);
    if (activeJob.state === "completed" && activeJob.result.interaction_id) {
      const nextId = activeJob.result.interaction_id;
      setThreadInteractionId((current) => current ? nextId : current);
    }
  }, [activeJob, mutateRecent]);

  const handleContinue = (job: JobInfo) => {
    setComposerCollapsed(false);
    setThreadInteractionId(job.result.interaction_id ?? null);
    if (typeof job.params.prompt === "string" && !currentPrompt.trim()) handleParamChange("prompt", job.params.prompt);
    requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-param-name="prompt"]')?.focus());
  };

  const handleGenerate = async () => {
    setSubmitError(null);
    if (!selectedModel || !selectedCapability) return;
    if (outputCount == null) { setSubmitError("Enter a positive whole number of outputs."); return; }
    if (currentCapability) {
      for (const param of currentCapability.ui_schema.params) {
        const value = effectiveParams[param.name];
        if (param.required && (value === undefined || value === null || value === "")) {
          setSubmitError(`"${param.label ?? param.name.replace(/_/g, " ")}" is required`);
          return;
        }
      }
    }
    setQuoteLoading(true);
    try {
      const quoteParams = threadInteractionId
        ? { ...effectiveParams, previous_interaction_id: threadInteractionId }
        : effectiveParams;
      const quote = await createGenerationQuote({
        model_key: selectedModel,
        capability_key: selectedCapability,
        params: quoteParams,
        project_id: selectedProjectId ?? undefined,
        generation_count: outputCount,
      });
      setCostQuote(quote);
      setCostDialogOpen(true);
    } catch (cause) {
      setSubmitError(errorMessage(cause, "Failed to prepare cost quote"));
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleEnhanceOnly = async () => {
    setSubmitError(null);
    const enhanced = await enhancer.enhance();
    if (enhanced) setPanel("director");
  };

  const handlePrimaryAction = async () => {
    if (!enhancerEnabled) {
      await handleGenerate();
      return;
    }
    if (!enhancer.result) {
      await handleEnhanceOnly();
      return;
    }
    if (!enhancer.isApplied) {
      await enhancer.apply();
      return;
    }
    await handleGenerate();
  };

  const confirmGeneration = async () => {
    if (!costQuote || confirmLoading) return;
    setConfirmLoading(true);
    setSubmitError(null);
    try {
      // Retrying a lost acceptance response keeps the same quote/key. Each new
      // Generate action creates a fresh quote and remains an independent job.
      const job = await submitQuotedJob(costQuote.id, `web-quote-${costQuote.id}`);
      if (!("batch_id" in job)) setActiveJobId(job.id);
      void mutateRecent();
      setCostDialogOpen(false);
      setCostQuote(null);
      setComposerCollapsed(true);
    } catch (cause) {
      if (cause instanceof ApiError && cause.details.code === "quote_refreshed" && Array.isArray(cause.details.quotes)) {
        const replacement = cause.details.quotes.find((item: { previous_quote_id?: string }) => item.previous_quote_id === costQuote.id) as { quote: CostQuoteInfo } | undefined;
        if (replacement?.quote) setCostQuote(replacement.quote);
      }
      setSubmitError(errorMessage(cause, "Failed to submit job"));
    } finally {
      setConfirmLoading(false);
    }
  };

  const dismissLibraryDraft = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("library_item"); next.delete("library_target");
    setSearchParams(next, { replace: true });
  };

  const useLibraryItem = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("library_item", id);
    setSearchParams(next);
    setAssetPickerOpen(false);
  };

  const openAssetPicker = (tab: AssetPickerTab) => {
    setAssetPickerTab(tab);
    setAssetPickerOpen(true);
  };

  const openDirectionsFromDirector = () => {
    setPanel(null);
    setDirectionOpen(true);
  };

  const activeInProgress = activeJob && !["completed", "failed", "cancelled"].includes(activeJob.state);
  const timelineJobs = activeInProgress && matchesGalleryMedia(activeJob, galleryMedia)
    ? [activeJob, ...recentJobs.filter((job) => job.id !== activeJob.id)]
    : recentJobs;
  const canvasJobs = timelineJobs.filter((job) => !["failed", "cancelled"].includes(job.state));
  const selection = useJobSelection(canvasJobs);
  const bulkDownload = useBulkDownload();
  const projectMove = useProjectMove({
    move: moveJobs,
    onMoved: () => {
      selection.clear();
      bulkDownload.reset();
      return mutateRecent();
    },
  });
  const [trashRequest, setTrashRequest] = useState<{ ids: string[]; skipped: number } | null>(null);
  const [trashBusy, setTrashBusy] = useState(false);
  const [trashError, setTrashError] = useState<string | null>(null);
  const openTrash = (targets: JobInfo[]) => {
    const manageable = targets.filter((job) => job.can_manage);
    if (!manageable.length) return;
    setTrashError(null);
    setTrashRequest({ ids: manageable.map((job) => job.id), skipped: targets.length - manageable.length });
  };
  const confirmTrash = async () => {
    if (!trashRequest || trashBusy) return;
    setTrashBusy(true);
    setTrashError(null);
    try {
      await trashJobs(trashRequest.ids);
      setTrashRequest(null);
      selection.clear();
      bulkDownload.reset();
    } catch (cause) {
      setTrashError(cause instanceof ApiError ? cause.message : "The selection could not be moved to Trash.");
    } finally {
      setTrashBusy(false);
      await mutateRecent();
    }
  };
  const openMove = (targets: JobInfo[]) => {
    const manageable = targets.filter((job) => job.can_manage);
    if (!manageable.length) return;
    projectMove.dismiss();
    projectMove.open(manageable.map((job) => job.id), {
      skipped: targets.length - manageable.length,
      currentProjectId: sharedProjectId(manageable),
    });
  };
  const selectionToolbar = selection.ids.size > 0 ? (
    <SelectionToolbar
      count={selection.ids.size}
      selectableCount={selection.selectableCount}
      onSelectAll={selection.selectAll}
      onClear={() => {
        selection.clear();
        bulkDownload.reset();
      }}
      onDownload={() => void bulkDownload.start(downloadPlan(canvasJobs, selection.ids))}
      download={bulkDownload.state}
      onMove={selection.selected.some((job) => job.can_manage) ? () => openMove(selection.selected) : undefined}
      onTrash={selection.selected.some((job) => job.can_manage) ? () => openTrash(selection.selected) : undefined}
      busy={projectMove.busy || trashBusy}
    />
  ) : projectMove.outcome ? (
    <MoveResultBar
      outcome={projectMove.outcome}
      busy={projectMove.busy}
      onUndo={() => void projectMove.undo()}
      onDismiss={projectMove.dismiss}
    />
  ) : null;
  const generationBusy = quoteLoading || confirmLoading;
  const primaryStage = !enhancerEnabled
    ? "generate"
    : !enhancer.result
      ? "enhance"
      : !enhancer.isApplied
        ? "apply"
        : "generate-enhanced";
  const primaryLabel = primaryStage === "enhance"
    ? "Improve prompt"
    : primaryStage === "apply"
      ? "Use this prompt"
      : primaryStage === "generate-enhanced"
        ? "Generate"
        : "Generate";
  const primaryLoading = generationBusy || enhancer.loading === "enhance" || enhancer.loading === "apply";
  const primaryLoadingLabel = enhancer.loading === "enhance"
    ? "Enhancing…"
    : enhancer.loading === "apply"
      ? "Applying…"
      : quoteLoading
        ? "Preparing quote…"
        : "Generating…";
  const primaryHint = primaryStage === "enhance"
    ? "Creates an editable prompt now. Video generation stays separate."
    : primaryStage === "apply"
      ? "Your revision is ready. Apply it before generation."
      : primaryStage === "generate-enhanced"
        ? "Improved prompt is in the composer. You confirm generation cost next."
        : "You confirm the exact cost before anything is generated or charged.";
  const primaryShowsGenerationCost = primaryStage === "generate" || primaryStage === "generate-enhanced";
  const referenceBlocker = primaryShowsGenerationCost
    ? validatedReferenceMessage(currentCapability, params, estimateKey, referenceEstimate)
    : null;
  const primaryDisabled =
    !selectedModel
    || !selectedCapability
    || (primaryShowsGenerationCost && (outputCount == null || promptMissing))
    || (primaryStage === "enhance" && currentPrompt.trim().length < 3)
    || Boolean(referenceBlocker);

  return (
    <div className="page-container flex min-h-full max-w-[1720px] flex-col pb-3 lg:pb-5">
      <header className="flex flex-col gap-2.5 border-b border-studio-border pb-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center justify-between gap-3 xl:justify-start">
          <h1 className="text-lg font-semibold tracking-tight text-studio-ink">Studio</h1>
          <WorkspaceModeTabs value={workspaceMode} onChange={handleWorkspaceModeChange} />
        </div>

        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-end">
          <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <StudioTool icon={FileText} label="Brief" active={panel === "brief"} onClick={() => setPanel(panel === "brief" ? null : "brief")} />
            <StudioTool icon={Boxes} label="Elements" onClick={() => openAssetPicker("elements")} />
            <StudioTool icon={Wand2} label="AI Director" emphasized active={panel === "director"} onClick={() => setPanel(panel === "director" ? null : "director")} />
            <StudioTool icon={History} label="Recent" count={timelineJobs.length} active={panel === "recent"} onClick={() => setPanel(panel === "recent" ? null : "recent")} />
          </div>
        </div>
      </header>

      {libraryPayload && libraryItemId && (
        <div data-testid="library-draft-banner" className="mt-3 flex items-start justify-between gap-3 rounded-xl border border-studio-accent/30 bg-studio-accent-soft px-3.5 py-3">
          <div className="flex min-w-0 items-start gap-2.5">
            <Library className="mt-0.5 h-4 w-4 shrink-0 text-studio-accent" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-studio-ink">Library draft loaded</p>
              <p className="mt-0.5 truncate text-[11px] text-studio-muted">{libraryPayload.title} · Review before generating; no job or wallet movement has started.</p>
              {libraryPayload.warnings.map((warning) => <p key={warning} className="mt-1 text-[11px] text-studio-warning">{warning}</p>)}
            </div>
          </div>
          <button type="button" onClick={dismissLibraryDraft} aria-label="Dismiss Library draft" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-studio-muted hover:bg-studio-panel hover:text-studio-ink"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {recreateNotice && (
        <div
          data-testid="recreate-banner"
          className={cn(
            "mt-3 flex items-start justify-between gap-3 rounded-xl border px-3.5 py-3",
            recreateNotice.tone === "error"
              ? "border-studio-danger/30 bg-studio-danger/10"
              : "border-studio-accent/30 bg-studio-accent-soft"
          )}
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <Repeat2 className={cn("mt-0.5 h-4 w-4 shrink-0", recreateNotice.tone === "error" ? "text-studio-danger" : "text-studio-accent")} />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-studio-ink">
                {recreateNotice.tone === "error" ? "Generation could not be restored" : "Generation restored"}
              </p>
              <p className="mt-0.5 text-[11px] text-studio-muted">
                {recreateNotice.message}
                {recreateNotice.tone === "info" && " · Review before generating; no job or wallet movement has started."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRecreateNotice(null)}
            aria-label="Dismiss restored generation"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-studio-muted hover:bg-studio-panel hover:text-studio-ink"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {libraryError && libraryItemId && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">
          <span>Could not load this Library draft.</span>
          <button type="button" onClick={dismissLibraryDraft} className="font-semibold underline">Clear</button>
        </div>
      )}

      <main className="relative flex min-h-[calc(100dvh-12rem)] flex-1 flex-col pt-3 lg:min-h-[calc(100dvh-7.5rem)]">
        {workspaceMode === "create" ? (
          <div className="space-y-4 pb-24">
            <section aria-label="Create workspace" className="overflow-hidden rounded-2xl border border-studio-border bg-studio-panel shadow-panel">
              <div className="p-3 sm:p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.15fr)_minmax(0,0.85fr)_auto] lg:items-end">
                  <div className="min-w-0">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Provider</label>
                    <ProviderPicker
                      models={models ?? []}
                      selected={selectedProvider}
                      onSelect={handleProviderChange}
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Model</label>
                    <ModelPicker
                      models={providerModels}
                      selected={selectedModel}
                      favorite={preferences?.favorite_model_key ?? null}
                      favorites={preferences?.favorite_model_keys ?? []}
                      onFavoriteChange={(key) => void updatePreferences({ favorite_model_key: key })}
                      onFavoritesChange={(keys) => void updatePreferences({ favorite_model_keys: keys })}
                      onSelect={handleModelChange}
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Capability</label>
                    {capabilities && capabilities.length > 1 ? (
                      <SegmentedControl
                        options={capabilities.map((capability) => ({ value: capability.key, label: capability.label }))}
                        value={selectedCapability}
                        onChange={(key) => {
                          setSelectedCapability(key);
                          setParams(defaultsFor(capabilities.find((capability) => capability.key === key)));
                          setDirections((current) => resetVisualDirections(current));
                        }}
                        className="sm:w-full"
                      />
                    ) : (
                      <div className="flex min-h-11 items-center rounded-xl border border-studio-border bg-studio-panel px-3 text-xs font-medium text-studio-ink">
                        {currentCapability?.label ?? (selectedProvider ? "Select a model" : "Select a provider")}
                      </div>
                    )}
                  </div>

                  {selectedModel && selectedCapability && (
                    <button
                      type="button"
                      onClick={() => void setCurrentAsDefault()}
                      aria-pressed={memoryPinned && isDefaultWorkflow}
                      aria-label={
                        memoryPinned && isDefaultWorkflow
                          ? "This model is pinned. Switch back to remembering the last model used"
                          : "Pin this model so Studio always opens with it"
                      }
                      title={
                        memoryPinned && isDefaultWorkflow
                          ? "Pinned — click to go back to the last model used"
                          : "Studio opens with the last model you used. Click to pin this one instead."
                      }
                      className={cn(
                        "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-[11px] font-semibold transition-colors",
                        memoryPinned && isDefaultWorkflow
                          ? "border-studio-success/30 bg-studio-success/10 text-studio-success"
                          : "border-studio-border bg-studio-panel text-studio-muted hover:border-studio-accent/35 hover:text-studio-accent"
                      )}
                    >
                      {memoryPinned && isDefaultWorkflow ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                      <span>{memoryLabel}</span>
                    </button>
                  )}
                </div>
              </div>

              {threadInteractionId && (
                <div className="flex items-center justify-between gap-3 border-t border-studio-border bg-studio-accent-soft px-4 py-2.5 text-[11px] text-studio-accent">
                  <span className="flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5" /> Continuing from a previous result</span>
                  <button type="button" onClick={() => setThreadInteractionId(null)} className="rounded-md px-2 py-1 font-semibold hover:bg-studio-panel">Clear</button>
                </div>
              )}

              <div className="grid border-t border-studio-border xl:grid-cols-[minmax(0,1.3fr)_minmax(18rem,0.7fr)]">
                <div className="min-w-0 p-4 sm:p-5">
                  <div role="group" aria-label="Generation type" className="mb-4 max-w-sm"><span className="mb-1 block text-[10px] font-semibold text-studio-muted">Generate</span><ModeTabs value={modality} onChange={handleModalityChange} /></div>
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    {framingNames.length > 0 && <StudioQuickControls capability={framingCapability} params={params} onChange={handleParamChange} />}
                    <GenerationCount value={countInput} onChange={setCountInput} />
                  </div>
                  <div className="mb-4 flex flex-wrap items-center gap-1.5">
                    <span className="mr-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
                      <Film className="h-3.5 w-3.5 text-studio-accent" /> Direction
                    </span>
                    <DirectionChip label="Genre" value={directions.genre} onClick={() => setDirectionOpen(true)} />
                    <DirectionChip label="Style" value={lookDirectionLabel} onClick={() => setDirectionOpen(true)} />
                    <DirectionChip label="Camera" value={cameraDirectionLabel} onClick={() => setDirectionOpen(true)} />
                  </div>

                  <div className="empty:hidden mb-4">
                    <ComposerChips params={params} onChange={handleParamChange} />
                  </div>

                  {!currentCapability ? (
                    <div className="flex min-h-48 items-center justify-center rounded-xl border border-dashed border-studio-border text-sm text-studio-muted">Choose a model to load its creative controls.</div>
                  ) : (
                    <CapabilityForm
                      modelKey={selectedModel ?? ""}
                      capability={currentCapability}
                      params={params}
                      onChange={handleParamChange}
                      section="creative"
                      compact
                    />
                  )}

                  {(enhancerEnabled || (enhancerSupported && enhancer.result)) && <PromptEnhancerResult controller={enhancer} />}

                  {hasPrompt && (
                    <div className="mt-4">
                      <MentionPicker params={params} onChange={handleParamChange} />
                    </div>
                  )}
                </div>

                <section aria-label="Output controls" className="min-w-0 border-t border-studio-border bg-studio-card/45 p-4 sm:p-5 xl:border-l xl:border-t-0">
                  <button
                    type="button"
                    onClick={() => setOutputOpen((value) => !value)}
                    aria-expanded={outputOpen}
                    className="flex w-full items-center justify-between gap-2 text-left xl:pointer-events-none xl:cursor-default"
                  >
                    <span className="flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4 text-studio-accent" />
                      <span className="text-xs font-semibold text-studio-ink">Output controls</span>
                      {technicalParamCount > 0 && (
                        <span className="rounded-full bg-studio-card px-2 py-0.5 text-[10px] font-medium text-studio-muted">{technicalParamCount}</span>
                      )}
                    </span>
                    <ChevronDown className={cn("h-4 w-4 shrink-0 text-studio-muted transition-transform xl:hidden", outputOpen && "rotate-180")} />
                  </button>
                  <div className={cn("mt-4", !outputOpen && "hidden xl:block")}>
                    {technicalParamCount > 0 ? (
                      <CapabilityForm
                        modelKey={selectedModel ?? ""}
                        capability={currentCapability}
                        params={params}
                        onChange={handleParamChange}
                        section="technical"
                        omitParamNames={[...framingNames, ...NATIVE_COUNT_PARAMS]}
                        compact
                      />
                    ) : (
                      <p className="rounded-xl border border-dashed border-studio-border bg-studio-panel px-3 py-4 text-[11px] leading-5 text-studio-muted">This workflow has no additional output controls.</p>
                    )}
                  </div>
                </section>
              </div>

            </section>

            <div className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] z-10 lg:bottom-2">
              <div className="flex flex-col gap-2 rounded-2xl border border-studio-border-light bg-studio-panel/95 px-3 py-2.5 shadow-panel backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4">
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                  {enhancerSupported && (
                    <PromptEnhancerToggle
                      id="create-enhance-before-generation"
                      checked={enhancerEnabled}
                      disabled={enhancerSaving || primaryLoading}
                      onCheckedChange={(checked) => void handleEnhancerToggle(checked)}
                      compact
                    />
                  )}
                  {submitError || (enhancer.error && !enhancer.result) ? (
                    <p role="alert" className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{submitError ?? enhancer.error}</p>
                  ) : referenceBlocker ? (
                    <p role="status" className="rounded-lg bg-studio-warning-soft px-3 py-2 text-xs leading-4 text-studio-warning-ink">{referenceBlocker}</p>
                  ) : (
                    <p className="hidden text-[11px] leading-4 text-studio-muted md:block">{primaryHint}</p>
                  )}
                </div>
                <div className="w-full shrink-0 sm:w-56">
                  <GenerateButton
                    onClick={() => void handlePrimaryAction()}
                    disabled={primaryDisabled}
                    loading={primaryLoading}
                    loadingLabel={primaryLoadingLabel}
                    label={primaryLabel}
                    usdCost={primaryShowsGenerationCost ? totalEstimatedCost : undefined}
                    insufficientBalance={primaryShowsGenerationCost && insufficientBalance}
                  />
                </div>
              </div>
            </div>

            {(
              <section aria-label="Generation results" className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold text-studio-ink">Results</h2>
                  <span className="rounded-full bg-studio-card px-2 py-0.5 font-mono text-[10px] text-studio-muted">{canvasJobs.length}</span>
                </div>
                {selectionToolbar && <div className="flex justify-center">{selectionToolbar}</div>}
                <ResultGrid
                  jobs={canvasJobs}
                  onContinue={handleContinue}
                  onCancelled={() => void mutateRecent()}
                  liveJobId={activeInProgress ? activeJobId : null}
                  live={streaming ? { pct, message, partials } : null}
                  selectedIds={selection.ids}
                  onToggleSelect={selection.toggle}
                  onFavoriteChange={() => void mutateRecent()}
                  onMove={(job) => openMove([job])}
                  hasMore={hasMoreRecent}
                  onLoadMore={loadMoreRecentForViewer}
                />
                <LoadMoreSentinel hasMore={hasMoreRecent} loading={loadingMoreRecent} onLoadMore={loadMoreRecent} />
              </section>
            )}
          </div>
        ) : (
          <>
        <section
          aria-label="Generation canvas"
          className={cn("flex-1 md:pb-[28rem]", composerCollapsed ? "pb-28" : "pb-[65dvh]")}
        >
          {canvasJobs.length > 0 ? (
            <>
              {selectionToolbar && (
                <div className="fixed left-1/2 top-[calc(3.5rem+env(safe-area-inset-top)+0.5rem)] z-20 w-[calc(100%-1rem)] -translate-x-1/2 lg:sticky lg:left-auto lg:top-2 lg:mb-3 lg:flex lg:w-auto lg:translate-x-0 lg:justify-center">
                  {selectionToolbar}
                </div>
              )}
              <ResultGrid
                jobs={canvasJobs}
                onContinue={handleContinue}
                onCancelled={() => void mutateRecent()}
                liveJobId={activeInProgress ? activeJobId : null}
                live={streaming ? { pct, message, partials } : null}
                selectedIds={selection.ids}
                onToggleSelect={selection.toggle}
                onFavoriteChange={() => void mutateRecent()}
                onMove={(job) => openMove([job])}
                hasMore={hasMoreRecent}
                onLoadMore={loadMoreRecentForViewer}
              />
              <LoadMoreSentinel hasMore={hasMoreRecent} loading={loadingMoreRecent} onLoadMore={loadMoreRecent} />
            </>
          ) : (
            <div className="flex min-h-[38vh] items-center justify-center rounded-2xl border border-dashed border-studio-border px-6 text-center lg:min-h-[48vh]">
              <div className="max-w-md">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-studio-border bg-studio-panel text-studio-accent shadow-sm"><Film className="h-5 w-5" /></span>
                <h2 className="mt-4 text-base font-semibold text-studio-ink">Your production canvas is ready</h2>
                <p className="mx-auto mt-2 max-w-sm text-xs leading-5 text-studio-muted">Describe a scene below. Results, variations and live generation progress will stay in this canvas.</p>
                <button type="button" onClick={() => setPanel("director")} className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-lg border border-studio-border bg-studio-panel px-3 text-[11px] font-semibold text-studio-ink hover:border-studio-accent/35">
                  <Wand2 className="h-3.5 w-3.5 text-studio-accent" /> Start with AI Director
                </button>
              </div>
            </div>
          )}
        </section>

        <section
          data-testid="studio-composer"
          aria-label="Generation composer"
          className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] left-1/2 z-20 w-[calc(100%-1rem)] max-w-5xl -translate-x-1/2 rounded-2xl border border-studio-border-light bg-studio-panel/95 shadow-panel backdrop-blur sm:w-[calc(100%-2rem)] lg:bottom-5 lg:left-[calc(50%+7.5rem)] lg:w-[calc(100%-18rem)]"
        >
          <button
            type="button"
            aria-expanded={!composerCollapsed}
            aria-controls="chat-composer-body"
            onClick={() => setComposerCollapsed((collapsed) => !collapsed)}
            className="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-xs font-semibold text-studio-ink md:hidden"
          >
            <span className="shrink-0">{composerCollapsed ? "Edit prompt" : "Show results"}</span>
            <span className="min-w-0 truncate text-[11px] font-normal text-studio-muted">{composerCollapsed ? ((submitError || referenceBlocker) ? "Generation needs attention" : currentPrompt || "Describe a scene…") : `${canvasJobs.length} results`}</span>
            <ChevronDown className={cn("h-4 w-4 shrink-0", composerCollapsed && "rotate-180")} />
          </button>
          <div id="chat-composer-body" className={cn("max-h-[60dvh] overflow-y-auto overscroll-contain md:max-h-none md:overflow-visible", composerCollapsed && "hidden md:block")}>
          <div className="flex flex-col items-center gap-1.5 px-2 pt-2 md:absolute md:bottom-full md:left-0 md:right-0 md:mb-2 md:pt-0">
            <ComposerChips params={params} onChange={handleParamChange} />
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <DirectionChip label="Genre" value={directions.genre} onClick={() => setDirectionOpen(true)} />
              <DirectionChip label="Style" value={lookDirectionLabel} onClick={() => setDirectionOpen(true)} />
              <DirectionChip label="Camera" value={cameraDirectionLabel} onClick={() => setDirectionOpen(true)} />
            </div>
          </div>

          {threadInteractionId && (
            <div className="flex items-center justify-between gap-3 border-b border-studio-border px-3 py-2 text-[11px] text-studio-accent sm:px-4">
              <span className="flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5" /> Continuing from a previous result</span>
              <button type="button" onClick={() => setThreadInteractionId(null)} className="rounded-md px-2 py-1 font-semibold hover:bg-studio-card">Clear</button>
            </div>
          )}

          <div className="p-2 sm:p-2.5">
            <div className="grid grid-cols-[minmax(0,1fr)_6.75rem] gap-2 md:grid-cols-[minmax(0,1fr)_15rem_7.5rem] md:items-end">
              <div className="col-span-2 min-w-0 rounded-xl bg-studio-card px-2.5 pb-2 pt-1.5 md:col-span-1">
              {workspaceMode === "chat" && (
                <div className="flex items-center gap-2 px-1.5 pt-1 text-[10px] font-semibold text-studio-accent">
                  <MessageSquare className="h-3.5 w-3.5" /> Chat Director
                  <span className="font-normal text-studio-muted">Describe, revise or continue a scene</span>
                </div>
              )}
              <div className="relative">
                <PromptEditor
                  key={`${selectedModel}:${selectedCapability}`}
                  ref={promptRef}
                  value={currentPrompt}
                  params={params}
                  disabled={!hasPrompt}
                  onChange={(next, binding) => {
                    if (binding) handleParamChange("reference_bindings", [...referenceBindings(params).filter((item) => item.token !== binding.token), binding]);
                    handleParamChange("prompt", next);
                  }}
                  onSubmit={() => void handlePrimaryAction()}
                  placeholder={hasPrompt
                    ? workspaceMode === "chat"
                      ? "Describe a scene, request a variation, or continue a previous result…"
                      : "Describe what you want to create…"
                    : "This workflow does not accept a text prompt."}
                  className={cn(
                    "w-full border-0 bg-transparent px-1.5 py-2 text-studio-ink",
                    workspaceMode === "chat" ? "max-h-40 min-h-20" : "max-h-24 min-h-10"
                  )}
                />
              </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-studio-border pt-2">
                  <span className={ZONE_LABEL}>Input</span>
                  <button
                    type="button"
                    onClick={() => openAssetPicker(uploadNames.length ? "uploads" : "elements")}
                    aria-label="Add assets or references"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-studio-border bg-studio-panel text-studio-muted hover:bg-studio-card-hover hover:text-studio-ink sm:h-9 sm:w-9"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => promptRef.current?.mention()}
                    aria-label="Mention an element"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-studio-border bg-studio-panel text-studio-muted hover:bg-studio-card-hover hover:text-studio-ink sm:h-9 sm:w-9"
                  >
                    <AtSign className="h-3.5 w-3.5" />
                  </button>
                  <span className={ZONE_DIVIDER} aria-hidden />
                  <span className={ZONE_LABEL}>Output</span>
                  <StudioQuickControls capability={singleOutputCapability} params={params} onChange={handleParamChange} />
                  <GenerationCount value={countInput} onChange={setCountInput} />
                  <span className={ZONE_DIVIDER} aria-hidden />
                  <span className={ZONE_LABEL}>Session</span>

                  <button
                    data-testid="advanced-settings-trigger"
                    type="button"
                    onClick={() => setAdvancedOpen((value) => !value)}
                    aria-label="Advanced settings"
                    aria-expanded={advancedOpen}
                    className={cn(
                      "inline-flex h-10 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-semibold transition-colors sm:h-9",
                      advancedOpen ? "border-studio-accent/35 bg-studio-accent-soft text-studio-accent" : "border-studio-border bg-studio-panel text-studio-muted hover:bg-studio-card-hover hover:text-studio-ink"
                    )}
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    {advancedCount > 0 && <span>{advancedCount}</span>}
                  </button>
                  {selectedModel && selectedCapability && (
                    <button
                      type="button"
                      onClick={() => void setCurrentAsDefault()}
                      aria-label={
                        memoryPinned && isDefaultWorkflow
                          ? "This model is pinned. Switch back to remembering the last model used"
                          : "Pin this model so Studio always opens with it"
                      }
                      title={
                        memoryPinned && isDefaultWorkflow
                          ? "Pinned — click to go back to the last model used"
                          : "Studio opens with the last model you used. Click to pin this one instead."
                      }
                      aria-pressed={memoryPinned && isDefaultWorkflow}
                      className={cn(
                        "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-semibold sm:h-9",
                        memoryPinned && isDefaultWorkflow
                          ? "border-studio-success/30 bg-studio-success/10 text-studio-success"
                          : "border-studio-border bg-studio-panel text-studio-muted hover:bg-studio-card-hover hover:text-studio-accent"
                      )}
                    >
                      {memoryPinned && isDefaultWorkflow ? <BookmarkCheck className="h-3.5 w-3.5" /> : <BookmarkPlus className="h-3.5 w-3.5" />}
                      <span>{memoryLabel}</span>
                    </button>
                  )}
                  {enhancerSupported && (
                    <PromptEnhancerToggle
                      id="composer-enhance-before-generation"
                      checked={enhancerEnabled}
                      disabled={enhancerSaving || primaryLoading}
                      onCheckedChange={(checked) => void handleEnhancerToggle(checked)}
                      compact
                    />
                  )}
                </div>
              </div>

              <div className="flex min-h-[6.75rem] min-w-0 flex-col justify-center gap-1 rounded-xl border border-studio-border bg-studio-card p-1.5 md:self-end">
                  <div role="group" aria-label="Generation type"><ModeTabs value={modality} onChange={handleModalityChange} compact /></div>
                  <ProviderPicker
                    models={models ?? []}
                    selected={selectedProvider}
                    onSelect={handleProviderChange}
                    compact
                  />
                  <select aria-label="Model" value={selectedModel ?? ""} onChange={(event) => handleModelChange(event.target.value)} className="min-h-9 w-full min-w-0 rounded-lg bg-studio-panel px-2 text-xs text-studio-ink md:hidden">
                    <option value="" disabled>Select model</option>
                    {providerModels.map((model) => <option key={model.key} value={model.key}>{model.display_name}</option>)}
                  </select>
                  <div className="hidden md:block">
                  <ModelPicker
                    models={providerModels}
                    selected={selectedModel}
                    favorite={preferences?.favorite_model_key ?? null}
                    favorites={preferences?.favorite_model_keys ?? []}
                    onFavoriteChange={(key) => void updatePreferences({ favorite_model_key: key })}
                    onFavoritesChange={(keys) => void updatePreferences({ favorite_model_keys: keys })}
                    onSelect={handleModelChange}
                    placement="top"
                    compact
                    showFavorite={false}
                  />
                  </div>

                {capabilities && capabilities.length > 1 ? (
                  <label className="inline-flex min-h-7 min-w-0 items-center px-1.5 text-[10px] text-studio-muted">
                    <span className="mr-1.5 shrink-0 font-medium">Mode</span>
                    <select
                      aria-label="Capability"
                      value={selectedCapability ?? ""}
                      onChange={(event) => {
                        const key = event.target.value;
                        setSelectedCapability(key);
                        setParams(defaultsFor(capabilities.find((capability) => capability.key === key)));
                        setDirections((current) => resetVisualDirections(current));
                      }}
                      className="min-w-0 flex-1 appearance-none bg-transparent font-semibold text-studio-ink outline-none"
                    >
                      {capabilities.map((capability) => <option key={capability.key} value={capability.key}>{capability.label}</option>)}
                    </select>
                  </label>
                ) : (
                  <span className="truncate px-1.5 text-[10px] font-medium text-studio-muted">{currentCapability?.label ?? (selectedProvider ? "Select model" : "Select provider")}</span>
                )}
              </div>

              <GenerateButton
                onClick={() => void handlePrimaryAction()}
                disabled={primaryDisabled}
                loading={primaryLoading}
                loadingLabel={primaryLoadingLabel}
                label={primaryLabel}
                usdCost={primaryShowsGenerationCost ? totalEstimatedCost : undefined}
                insufficientBalance={primaryShowsGenerationCost && insufficientBalance}
                className="h-[4.5rem] min-h-0 flex-col gap-1 whitespace-normal rounded-xl px-2 text-center leading-4 md:self-end"
              />
            </div>
            {submitError
              ? <p role="alert" className="mt-2 rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{submitError}</p>
              : referenceBlocker && <p role="status" className="mt-2 rounded-lg bg-studio-warning-soft px-3 py-2 text-xs leading-4 text-studio-warning-ink">{referenceBlocker}</p>}
          </div>
          </div>
        </section>
          </>
        )}
      </main>

      <Dialog
        open={advancedOpen && Boolean(currentCapability)}
        onClose={() => setAdvancedOpen(false)}
        title="Advanced settings"
        placement="right"
        className="sm:max-w-md"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-5">
          <p className="mb-4 text-[11px] leading-5 text-studio-muted">Provider-native inputs and delivery controls.</p>
          {advancedCount > 0 && currentCapability ? (
            <CapabilityForm
              modelKey={selectedModel ?? ""}
              capability={currentCapability}
              params={params}
              onChange={handleParamChange}
              omitParamNames={advancedOmissions}
              compact
            />
          ) : <p className="rounded-lg border border-dashed border-studio-border p-4 text-center text-xs text-studio-muted">All available controls are already shown in the quick bar.</p>}
        </div>
      </Dialog>

      <ProjectBriefPanel open={panel === "brief"} onClose={() => setPanel(null)} projectId={selectedProjectId} />
      <StudioDirectorPanel
        open={panel === "director"}
        onClose={() => setPanel(null)}
        prompt={currentPrompt}
        directions={directions}
        modality={modality}
        enhancerSupported={enhancerSupported}
        enhancerEnabled={enhancerEnabled}
        enhancerSaving={enhancerSaving}
        enhancer={enhancer}
        onToggleEnhancer={(checked) => void handleEnhancerToggle(checked)}
        onEnhance={() => void handleEnhanceOnly()}
        onApplyPrompt={(value) => handleParamChange("prompt", value)}
        onOpenDirections={openDirectionsFromDirector}
      />
      <Dialog open={panel === "recent"} onClose={() => setPanel(null)} title={`Recent generations · ${timelineJobs.length}`} placement="right" className="sm:max-w-sm">
        <div className="flex-1 overflow-y-auto p-4">
          <ResultGrid
            jobs={timelineJobs}
            variant="compact"
            onContinue={(job) => { handleContinue(job); setPanel(null); }}
            onCancelled={() => void mutateRecent()}
            liveJobId={activeInProgress ? activeJobId : null}
            live={streaming ? { pct, message, partials } : null}
            onFavoriteChange={() => void mutateRecent()}
            hasMore={hasMoreRecent}
            onLoadMore={loadMoreRecentForViewer}
          />
          <LoadMoreSentinel hasMore={hasMoreRecent} loading={loadingMoreRecent} onLoadMore={loadMoreRecent} />
        </div>
      </Dialog>
      <MoveToProjectDialog
        open={projectMove.request !== null}
        count={projectMove.request?.ids.length ?? 0}
        noun="generation"
        currentProjectId={projectMove.request?.currentProjectId}
        noProjectHint="Shown only under All projects"
        busy={projectMove.busy}
        error={projectMove.error}
        onClose={projectMove.close}
        onConfirm={(projectId, name) => void projectMove.confirm(projectId, name)}
      />
      <StudioAssetPicker
        open={assetPickerOpen}
        initialTab={assetPickerTab}
        onClose={() => setAssetPickerOpen(false)}
        capability={currentCapability}
        modelKey={selectedModel ?? ""}
        params={params}
        onParamChange={handleParamChange}
        jobs={canvasJobs}
        onContinue={handleContinue}
        onUseLibrary={useLibraryItem}
        onCancelled={() => void mutateRecent()}
      />
      <StudioDirectionPicker
        open={directionOpen}
        initialItemId={searchParams.get("direction_item")}
        onClose={() => { setDirectionOpen(false); const next = new URLSearchParams(searchParams); next.delete("direction_item"); next.delete("direction_modality"); setSearchParams(next, { replace: true }); }}
        value={directions}
        onChange={setDirections}
        modality={modality}
        modelKey={selectedModel}
        capabilityKey={selectedCapability}
      />
      <TrashSelectionDialog
        open={trashRequest !== null}
        count={trashRequest?.ids.length ?? 0}
        skipped={trashRequest?.skipped ?? 0}
        busy={trashBusy}
        error={trashError}
        onCancel={() => setTrashRequest(null)}
        onConfirm={() => void confirmTrash()}
      />
      <CostConfirmationDialog
        quote={costQuote}
        open={costDialogOpen}
        loading={confirmLoading}
        error={submitError}
        onClose={() => {
          if (confirmLoading) return;
          setCostDialogOpen(false);
          setCostQuote(null);
        }}
        onConfirm={() => void confirmGeneration()}
        onImprovePrompt={enhancerSupported ? () => {
          setCostDialogOpen(false);
          setCostQuote(null);
          void handleEnhanceOnly();
        } : undefined}
      />
    </div>
  );
}

function StudioTool({
  icon: Icon,
  label,
  count,
  active = false,
  emphasized = false,
  onClick,
}: {
  icon: typeof Sparkles;
  label: string;
  count?: number;
  active?: boolean;
  emphasized?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex min-h-10 min-w-0 items-center justify-center gap-1.5 rounded-lg border px-2 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm sm:shrink-0 sm:px-2.5",
        active || emphasized
          ? "border-studio-accent/35 bg-studio-accent-soft text-studio-accent"
          : "border-studio-border bg-studio-panel text-studio-muted hover:border-studio-border-light hover:text-studio-ink"
      )}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
      {count != null && <span className="rounded-full bg-studio-card px-1.5 py-0.5 font-mono text-[10px]">{count}</span>}
    </button>
  );
}

function WorkspaceModeTabs({ value, onChange }: { value: WorkspaceMode; onChange: (value: WorkspaceMode) => void }) {
  const options = [
    { value: "create" as const, label: "Create", icon: SlidersHorizontal },
    { value: "chat" as const, label: "Chat", icon: MessageSquare },
  ];

  return (
    <div role="tablist" aria-label="Studio workspace mode" className="grid shrink-0 grid-cols-2 rounded-xl border border-studio-border bg-studio-card p-1">
      {options.map((option) => {
        const active = value === option.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm sm:min-h-8",
              active ? "bg-studio-panel text-studio-ink shadow-sm" : "text-studio-muted hover:text-studio-ink"
            )}
          >
            <Icon className="h-3.5 w-3.5" /> {option.label}
          </button>
        );
      })}
    </div>
  );
}

function DirectionChip({ label, value, onClick }: { label: string; value: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-9 max-w-[9.5rem] items-center gap-1.5 rounded-full border border-studio-border-light bg-studio-panel/95 px-3 text-[11px] shadow-sm backdrop-blur transition-colors hover:border-studio-accent/35 hover:bg-studio-card focus-visible:outline-none focus-visible:shadow-glow-sm"
    >
      <span className="shrink-0 text-studio-muted">{label}</span>
      <span className="truncate font-semibold text-studio-ink">{value}</span>
    </button>
  );
}
